import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Checkbox,
  ListItemText as MuiListItemText,
  OutlinedInput,
  FormHelperText,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  VideoLibrary,
  Description,
  School,
  Help,
  Public,
  Lock,
  Group,
  OndemandVideo,
  Article,
  MenuBook,
  AttachFile,
  CloudUpload,
  Download,
  DeleteOutline,
  People,
  Event as EventIcon,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const Content = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [contents, setContents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'notes',
    video_url: '',
    access_level: 'student',
    visibility_type: 'all',
    batch_id: '',
    student_ids: [],
    event_ids: [],
    is_active: true,
    file: null,
    file_path: '',
    file_type: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');

  const contentTypes = [
    { value: 'circular', label: 'Circular', icon: <Article /> },
    { value: 'notes', label: 'Notes', icon: <Description /> },
    { value: 'jewellery_costume_guide', label: 'Jewellery and Costume Guide', icon: <School /> },
    { value: 'video', label: 'Video', icon: <OndemandVideo /> },
    { value: 'faq', label: 'FAQ', icon: <Help /> },
    { value: 'model_question_papers', label: 'Model Question Papers', icon: <MenuBook /> },
    { value: 'newsletter', label: 'Newsletter', icon: <Article /> },
  ];

  // Map old content types to new values for backward compatibility
  const contentTypeMapping = {
    'costume_guide': 'jewellery_costume_guide',
    'jewellery_guide': 'jewellery_costume_guide',
    'glossary': 'model_question_papers'
  };

  const accessLevels = [
    { value: 'public', label: 'Public', icon: <Public />, color: 'success' },
    { value: 'student', label: 'Students Only', icon: <Lock />, color: 'primary' },
    { value: 'batch_specific', label: 'Batch Specific', icon: <Group />, color: 'secondary' },
  ];

  useEffect(() => {
    fetchContents();
    fetchBatches();
    fetchStudents();
    fetchEvents();
  }, []); // Only fetch once on mount, filtering is done client-side

  const fetchContents = async () => {
    try {
      // Always fetch all content, filtering is done client-side
      const response = await axios.get('/api/content/');
      setContents(response.data.contents);
    } catch (error) {
      toast.error('Failed to load content');
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/admin/batches');
      setBatches(response.data.batches);
    } catch (error) {
      console.error('Failed to load batches');
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/admin/students', { params: { per_page: 1000 } });
      setStudents(response.data.students);
    } catch (error) {
      console.error('Failed to load students');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/event/');
      setEvents(response.data.events);
    } catch (error) {
      console.error('Failed to load events');
    }
  };

  const handleDialogOpen = (content = null) => {
    if (content) {
      setEditMode(true);
      setSelectedContent(content);
      setFormData({
        title: content.title,
        description: content.description || '',
        content_type: content.content_type,
        video_url: content.video_url || '',
        access_level: content.access_level,
        visibility_type: content.visibility_type || 'all',
        batch_id: content.batch_id || '',
        student_ids: content.specific_student_ids || [],
        event_ids: content.specific_event_ids || [],
        is_active: content.is_active,
        file: null,
        file_path: content.file_path || '',
        file_type: content.file_type || '',
      });
      setSelectedFileName(content.file_path ? content.file_path.split('/').pop() : '');
    } else {
      setEditMode(false);
      setSelectedContent(null);
      setFormData({
        title: '',
        description: '',
        content_type: 'notes',
        video_url: '',
        access_level: 'student',
        visibility_type: 'all',
        batch_id: '',
        student_ids: [],
        event_ids: [],
        is_active: true,
        file: null,
        file_path: '',
        file_type: '',
      });
      setSelectedFileName('');
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedContent(null);
    setFormData({
      title: '',
      description: '',
      content_type: 'notes',
      video_url: '',
      access_level: 'student',
      visibility_type: 'all',
      batch_id: '',
      student_ids: [],
      event_ids: [],
      is_active: true,
      file: null,
      file_path: '',
      file_type: '',
    });
    setSelectedFileName('');
    setFormErrors({});
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, BMP, WEBP');
        e.target.value = '';
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({ ...prev, file }));
      setSelectedFileName(file.name);
      if (formErrors.file) {
        setFormErrors(prev => ({ ...prev, file: '' }));
      }
    }
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({ ...prev, file: null, file_path: '', file_type: '' }));
    setSelectedFileName('');
    // Reset file input
    const fileInput = document.getElementById('file-upload-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }
    if (!formData.content_type) {
      errors.content_type = 'Content type is required';
    }
    if (formData.content_type === 'video' && !formData.video_url.trim()) {
      errors.video_url = 'Video URL is required for video content';
    }
    if (formData.access_level === 'batch_specific' && !formData.batch_id) {
      errors.batch_id = 'Batch is required for batch-specific content';
    }
    if (formData.visibility_type === 'batch' && !formData.batch_id) {
      errors.batch_id = 'Batch is required for batch visibility';
    }
    if (formData.visibility_type === 'specific_students' && (!formData.student_ids || formData.student_ids.length === 0)) {
      errors.student_ids = 'At least one student must be selected';
    }
    if (formData.visibility_type === 'event' && (!formData.event_ids || formData.event_ids.length === 0)) {
      errors.event_ids = 'At least one event must be selected';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      // Use FormData if there's a file to upload, otherwise use JSON
      let submitData;
      let config = {};

      if (formData.file) {
        submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('description', formData.description || '');
        submitData.append('content_type', formData.content_type);
        submitData.append('video_url', formData.video_url || '');
        submitData.append('access_level', formData.access_level);
        submitData.append('visibility_type', formData.visibility_type);
        submitData.append('batch_id', formData.batch_id || '');
        submitData.append('student_ids', formData.student_ids.join(','));
        submitData.append('event_ids', formData.event_ids.join(','));
        submitData.append('is_active', formData.is_active);
        submitData.append('file', formData.file);
        
        config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
      } else {
        submitData = {
          title: formData.title,
          description: formData.description,
          content_type: formData.content_type,
          video_url: formData.video_url,
          access_level: formData.access_level,
          visibility_type: formData.visibility_type,
          batch_id: formData.batch_id || null,
          student_ids: formData.student_ids,
          event_ids: formData.event_ids,
          is_active: formData.is_active,
          file_path: formData.file_path,
          file_type: formData.file_type,
        };
      }
      
      if (editMode) {
        await axios.put(`/api/content/${selectedContent.id}`, submitData, config);
        toast.success('Content updated successfully');
      } else {
        await axios.post('/api/content/', submitData, config);
        toast.success('Content created successfully');
      }
      handleDialogClose();
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} content`);
    }
  };

  const handleDeleteClick = (content) => {
    setContentToDelete(content);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/content/${contentToDelete.id}`);
      toast.success('Content deleted successfully');
      setDeleteDialogOpen(false);
      setContentToDelete(null);
      fetchContents();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete content');
    }
  };

  const handleMenuOpen = (event, content) => {
    setAnchorEl(event.currentTarget);
    setSelectedContent(content);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFile = async (contentId, fileName) => {
    try {
      const response = await axios.get(`/api/content/download/${contentId}`, {
        responseType: 'blob',
      });
      
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getContentTypeIcon = (type) => {
    const mappedType = contentTypeMapping[type] || type;
    const contentType = contentTypes.find(ct => ct.value === mappedType);
    return contentType ? contentType.icon : <Description />;
  };

  const getContentTypeLabel = (type) => {
    const mappedType = contentTypeMapping[type] || type;
    const contentType = contentTypes.find(ct => ct.value === mappedType);
    return contentType ? contentType.label : type;
  };

  const getAccessLevelInfo = (level) => {
    const accessLevel = accessLevels.find(al => al.value === level);
    return accessLevel || { label: level, color: 'default', icon: <Lock /> };
  };

  const filteredContents = filterType === 'all' 
    ? contents 
    : contents.filter(c => {
        const mappedType = contentTypeMapping[c.content_type] || c.content_type;
        return mappedType === filterType;
      });

  const contentTypeCounts = contentTypes.reduce((acc, type) => {
    acc[type.value] = contents.filter(c => {
      const mappedType = contentTypeMapping[c.content_type] || c.content_type;
      return mappedType === type.value;
    }).length;
    return acc;
  }, { all: contents.length });

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3 
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Content Library
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage circulars, notes, guides, videos, and FAQs
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleDialogOpen()}
            fullWidth={isMobile}
          >
            Add Content
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3, width: '100%', ml: 0 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <VideoLibrary color="primary" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Total Content
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {contents.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <OndemandVideo color="error" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Videos
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {contentTypeCounts.video || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Description color="info" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Documents
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {(contentTypeCounts.notes || 0) + (contentTypeCounts.circular || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <School color="success" />
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Guides
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {(contentTypeCounts.costume_guide || 0) + (contentTypeCounts.jewellery_guide || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={filterType}
            onChange={(e, newValue) => setFilterType(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`All (${contentTypeCounts.all})`} value="all" />
            {contentTypes.map((type) => (
              <Tab
                key={type.value}
                label={`${type.label} (${contentTypeCounts[type.value] || 0})`}
                value={type.value}
                icon={type.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Card>

        {/* Content Table */}
        <Card>
          <CardContent>
            {isMobile ? (
              /* Mobile Card View */
              <Stack spacing={2}>
                {filteredContents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No content found. Click "Add Content" to create one.
                  </Typography>
                ) : (
                  filteredContents.map((content) => {
                    const accessInfo = getAccessLevelInfo(content.access_level);
                    return (
                      <Card 
                        key={content.id} 
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                          border: '1px solid',
                          borderColor: 'rgba(212, 175, 55, 0.2)',
                          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                            transform: 'translateY(-2px)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '4px',
                            height: '100%',
                            background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                          },
                        }}
                      >
                        <CardContent sx={{ pl: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                              <Box sx={{ color: '#D4AF37' }}>
                                {getContentTypeIcon(content.content_type)}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }}>
                                  {content.title}
                                </Typography>
                                {content.description && (
                                  <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.8rem' }}>
                                    {content.description.substring(0, 60)}
                                    {content.description.length > 60 ? '...' : ''}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, content)}
                              size="small"
                              sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                            >
                              <MoreVert />
                            </IconButton>
                          </Box>
                          
                          <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                          
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                                Type & Access
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                <Chip 
                                  label={getContentTypeLabel(content.content_type)} 
                                  size="small" 
                                  sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)', fontWeight: 600 }}
                                  variant="outlined"
                                />
                                <Chip
                                  icon={accessInfo.icon}
                                  label={accessInfo.label}
                                  size="small"
                                  color={accessInfo.color}
                                />
                              </Box>
                            </Box>
                            
                            {content.file_path && (
                              <Box>
                                <Typography variant="caption" sx={{ color: '#8B7355', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }} display="block">
                                  Attachment
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDownloadFile(content.id, content.file_path.split('/').pop())}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                  <Typography variant="caption" color="text.secondary">
                                    {content.file_path.split('/').pop()}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            
                            {content.video_url && (
                              <Box>
                                <Chip
                                  icon={<OndemandVideo />}
                                  label="Video"
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              </Box>
                            )}
                            
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {content.batch_id && (
                                <Chip 
                                  label={batches.find(b => b.id === content.batch_id)?.name || `Batch ${content.batch_id}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              {content.is_active ? (
                                <Chip label="Active" color="success" size="small" />
                              ) : (
                                <Chip label="Inactive" color="error" size="small" />
                              )}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Attachment</strong></TableCell>
                    <TableCell><strong>Access Level</strong></TableCell>
                    <TableCell><strong>Batch</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No content found. Click "Add Content" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredContents.map((content) => {
                      const accessInfo = getAccessLevelInfo(content.access_level);
                      return (
                        <TableRow key={content.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getContentTypeIcon(content.content_type)}
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {content.title}
                                </Typography>
                                {content.description && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {content.description.substring(0, 60)}
                                    {content.description.length > 60 ? '...' : ''}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getContentTypeLabel(content.content_type)} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>
                            {content.file_path ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadFile(content.id, content.file_path.split('/').pop())}
                                  title="Download file"
                                >
                                  <Download fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" color="text.secondary">
                                  {content.file_path.split('/').pop()}
                                </Typography>
                              </Box>
                            ) : content.video_url ? (
                              <Chip
                                icon={<OndemandVideo />}
                                label="Video"
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={accessInfo.icon}
                              label={accessInfo.label}
                              size="small"
                              color={accessInfo.color}
                            />
                          </TableCell>
                          <TableCell>
                            {content.batch_id ? (
                              <Chip 
                                label={batches.find(b => b.id === content.batch_id)?.name || `Batch ${content.batch_id}`}
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {content.is_active ? (
                              <Chip label="Active" color="success" size="small" />
                            ) : (
                              <Chip label="Inactive" color="error" size="small" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, content)}
                              size="small"
                            >
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleDialogOpen(selectedContent);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleDeleteClick(selectedContent)}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add/Edit Content Dialog */}
        <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editMode ? 'Edit Content' : 'Add New Content'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    error={!!formErrors.title}
                    helperText={formErrors.title}
                    required
                    placeholder="e.g., Basic Adavus Tutorial"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                    placeholder="Brief description of the content"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required error={!!formErrors.content_type}>
                    <InputLabel>Content Type</InputLabel>
                    <Select
                      name="content_type"
                      value={formData.content_type}
                      onChange={handleFormChange}
                      label="Content Type"
                    >
                      {contentTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {type.icon}
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      name="access_level"
                      value={formData.access_level}
                      onChange={handleFormChange}
                      label="Access Level"
                    >
                      {accessLevels.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {level.icon}
                            {level.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.content_type === 'video' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Video URL"
                      name="video_url"
                      value={formData.video_url}
                      onChange={handleFormChange}
                      error={!!formErrors.video_url}
                      helperText={formErrors.video_url || 'YouTube, Vimeo, or direct video URL'}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </Grid>
                )}
                
                {/* File Upload Section */}
                {formData.content_type !== 'video' && (
                  <Grid item xs={12}>
                    <Box sx={{ 
                      border: '2px dashed', 
                      borderColor: 'divider', 
                      borderRadius: 2, 
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'background.default'
                    }}>
                      <input
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp"
                        style={{ display: 'none' }}
                        id="file-upload-input"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="file-upload-input">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
                          <Box>
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<AttachFile />}
                            >
                              Choose File
                            </Button>
                            <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                              Allowed: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, BMP, WEBP (Max 10MB)
                            </Typography>
                          </Box>
                        </Box>
                      </label>
                      
                      {(selectedFileName || formData.file_path) && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 2, 
                          bgcolor: 'background.paper', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachFile color="primary" />
                            <Typography variant="body2">
                              {selectedFileName || (formData.file_path ? formData.file_path.split('/').pop() : '')}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleRemoveFile}
                            title="Remove file"
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
                
                {formData.access_level === 'batch_specific' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!formErrors.batch_id}>
                      <InputLabel>Select Batch</InputLabel>
                      <Select
                        name="batch_id"
                        value={formData.batch_id}
                        onChange={handleFormChange}
                        label="Select Batch"
                      >
                        <MenuItem value="">None</MenuItem>
                        {batches.filter(b => b.is_active).map((batch) => (
                          <MenuItem key={batch.id} value={batch.id}>
                            {batch.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                
                {/* Visibility Options */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Visibility Settings
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Who can view this content?</InputLabel>
                    <Select
                      name="visibility_type"
                      value={formData.visibility_type}
                      onChange={handleFormChange}
                      label="Who can view this content?"
                    >
                      <MenuItem value="all">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Public fontSize="small" />
                          All Students
                        </Box>
                      </MenuItem>
                      <MenuItem value="batch">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Group fontSize="small" />
                          Specific Batch
                        </Box>
                      </MenuItem>
                      <MenuItem value="specific_students">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <People fontSize="small" />
                          Specific Students
                        </Box>
                      </MenuItem>
                      <MenuItem value="event">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventIcon fontSize="small" />
                          Event Participants
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Batch Selection for Visibility */}
                {formData.visibility_type === 'batch' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!formErrors.batch_id}>
                      <InputLabel>Select Batch</InputLabel>
                      <Select
                        name="batch_id"
                        value={formData.batch_id}
                        onChange={handleFormChange}
                        label="Select Batch"
                      >
                        <MenuItem value="">None</MenuItem>
                        {batches.filter(b => b.is_active).map((batch) => (
                          <MenuItem key={batch.id} value={batch.id}>
                            {batch.name} ({batch.student_count} students)
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.batch_id && (
                        <FormHelperText error>{formErrors.batch_id}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
                
                {/* Student Selection */}
                {formData.visibility_type === 'specific_students' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!formErrors.student_ids}>
                      <InputLabel>Select Students</InputLabel>
                      <Select
                        multiple
                        name="student_ids"
                        value={formData.student_ids}
                        onChange={handleFormChange}
                        input={<OutlinedInput label="Select Students" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const student = students.find(s => s.id === value);
                              return (
                                <Chip 
                                  key={value} 
                                  label={student?.student_name || `Student ${value}`} 
                                  size="small" 
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {students
                          .filter(s => s.is_approved && s.is_active)
                          .map((student) => (
                            <MenuItem key={student.id} value={student.id}>
                              <Checkbox checked={formData.student_ids.indexOf(student.id) > -1} />
                              <MuiListItemText 
                                primary={student.student_name} 
                                secondary={`${student.batch_name || 'No Batch'} • ${student.email}`}
                              />
                            </MenuItem>
                          ))}
                      </Select>
                      {formErrors.student_ids && (
                        <FormHelperText error>{formErrors.student_ids}</FormHelperText>
                      )}
                      <FormHelperText>
                        {formData.student_ids.length} student(s) selected
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                
                {/* Event Selection */}
                {formData.visibility_type === 'event' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required error={!!formErrors.event_ids}>
                      <InputLabel>Select Events</InputLabel>
                      <Select
                        multiple
                        name="event_ids"
                        value={formData.event_ids}
                        onChange={handleFormChange}
                        input={<OutlinedInput label="Select Events" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const event = events.find(e => e.id === value);
                              return (
                                <Chip 
                                  key={value} 
                                  label={event?.title || `Event ${value}`} 
                                  size="small" 
                                  color="secondary"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {events.map((event) => (
                          <MenuItem key={event.id} value={event.id}>
                            <Checkbox checked={formData.event_ids.indexOf(event.id) > -1} />
                            <MuiListItemText 
                              primary={event.title} 
                              secondary={`${event.event_type} • ${event.event_date ? new Date(event.event_date).toLocaleDateString() : 'No date'}`}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.event_ids && (
                        <FormHelperText error>{formErrors.event_ids}</FormHelperText>
                      )}
                      <FormHelperText>
                        {formData.event_ids.length} event(s) selected
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editMode ? 'Update' : 'Create'} Content
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Content</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete <strong>"{contentToDelete?.title}"</strong>?
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default Content;
